import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { hideRouteAlert, selectRouteAlert } from "@/reducers/overlaysSlice"
import { useCallback, useEffect, useRef, useState } from "react"
import MapView, { MapViewHandle } from "@/components/maps/MapView"
import AdHocCommands from "@/api/adHocCommands"
import RoutePolylinesParser from "@/lib/routePolylinesPareser"
import { RouteInfo } from "./RouteView"
import { Route2g } from "@/reducers/routes2gSlice"
import { convertToRoute2g } from "@/components/maps/SharedMap"
import DriverPolylineParser from "@/lib/driverPolylineParser"
import * as polyline from "@mapbox/polyline"
import { Source } from "./mapLibre/Source"
import { Layer, LayerProps } from "./mapLibre/Layer"
import { Feature, Geometry } from "geojson"
import { LngLatBounds } from "maplibre-gl"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import useChatInfo from "@/hooks/useChatInfo"
import { selectActiveChat } from "@/reducers/chatsSliceSelectors"
import { format } from "date-fns"
import PulsingDot from "@/components/maps/PulsingDot"
import { Popup } from "@/components/maps/mapLibre/Popup"
import { sumRouteMetrics } from "./mapUtils"
import { selectRouteJidFromChatJid } from "@/reducers/selectors"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

const referenceRouteLayer: LayerProps = {
  id: "reference-route-layer",
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
    visibility: "visible",
  },
  paint: {
    "line-color": "#0db368", // Green color for reference route
    "line-width": 4, // Wider than original route
    "line-opacity": 0.7,
    "line-gap-width": 6, // Creates a hollow effect with just borders
  },
}

const driverRouteLayer: LayerProps = {
  id: "driver-route-layer",
  type: "line",
  layout: {
    visibility: "visible",
  },
  paint: {
    "line-color": "#960018",
    "line-width": 3,
    "line-opacity": 1,
  },
}

const driverLocationLayer: LayerProps = {
  id: "location-point",
  type: "symbol",
  layout: {
    "icon-image": "pulsing-dot",
  },
}

function ReferenceRouteAlert() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const mapViewRef = useRef<MapViewHandle>(null)
  const referenceRouteAlert = useAppSelector(selectRouteAlert)
  const activeChat = useAppSelector(selectActiveChat)
  const { driverParticipant } = useChatInfo(activeChat)
  const routeJid = useAppSelector(selectRouteJidFromChatJid(activeChat.jid))

  // Track all timeouts for cleanup

  const [stops, setStops] = useState<Route2g | undefined>(undefined)
  const [routeInfo, setRouteInfo] = useState<RouteInfo | undefined>(undefined)
  const [driverLocationSource, setDriverLocationSource] = useState<
    Feature<Geometry, { [p: string]: any }> | undefined
  >(undefined)
  const [referenceRouteSource, setReferenceRouteSource] = useState<
    Feature<Geometry, { [p: string]: any }> | undefined
  >(undefined)
  const [driverRouteSource, setDriverRouteSource] = useState<
    Feature<Geometry, { [p: string]: any }> | undefined
  >(undefined)

  const [popupSettings, setPopupSettings] = useState<
    | {
        lat: number
        lon: number
        referenceRouteDistance: number
        currentRouteAndDriverDistance: number
        referenceTollCost: number | null
        projectedTollCost: number | null
      }
    | undefined
  >(undefined)

  const handleClose = useCallback(() => {
    dispatch(hideRouteAlert())
  }, [dispatch])

  // Draw reference route on the map - using polling to check style loaded

  const showAllLayers = useCallback(() => {
    const mapInstance = mapViewRef.current?.getMapManager()?.getMapInstance()
    if (!mapInstance) return
    const bounds = new LngLatBounds()

    // Extend bounds with reference route coordinates
    if (
      referenceRouteSource &&
      referenceRouteSource.geometry.type === "LineString"
    ) {
      referenceRouteSource.geometry.coordinates.forEach((coord) => {
        bounds.extend(coord as [number, number])
      })
    }

    // Extend bounds with driver route coordinates
    if (driverRouteSource && driverRouteSource.geometry.type === "LineString") {
      driverRouteSource.geometry.coordinates.forEach((coord) => {
        bounds.extend(coord as [number, number])
      })
    }
    if (!bounds.isEmpty()) {
      mapInstance.fitBounds(bounds, {
        padding: 40,
        maxZoom: 15,
        duration: 1000,
      })
    }
    if (driverLocationSource) {
      if (!mapInstance.hasImage("pulsing-dot")) {
        const pulsingDotImage = PulsingDot(mapInstance)
        mapInstance.addImage("pulsing-dot", pulsingDotImage, {
          pixelRatio: 2,
        })
      }
    }
  }, [driverLocationSource, driverRouteSource, referenceRouteSource])

  const fetchAlertData = useCallback(async () => {
    const { alertId } = referenceRouteAlert
    if (!routeJid || !alertId) return

    try {
      const result = await AdHocCommands.readReferenceRoute(
        window.XMPP!,
        routeJid,
        alertId,
      )

      const {
        reference_route: referenceRouteInfo,
        projected_route: projectedRoute,
        driver_location_during_alert: driverLocation,
        reference_toll_cost: referenceTollCost,
        projected_toll_cost: projectedTollCost,
      } = RoutePolylinesParser.parse(result)?.route as unknown as {
        reference_route: RouteInfo
        projected_route: RouteInfo
        driver_location_during_alert: { lat: number; lng: number }
        reference_toll_cost: number | null
        projected_toll_cost: number | null
      }

      if (projectedRoute && projectedRoute.stops && routeJid) {
        const fetchedStops = convertToRoute2g(projectedRoute.stops, routeJid)
        setStops(fetchedStops)
        setRouteInfo(projectedRoute)
        setDriverLocationSource({
          type: "Feature",
          properties: {},
          geometry: {
            type: "Point",
            coordinates: [driverLocation.lng, driverLocation.lat],
          },
        })

        if (
          referenceRouteInfo &&
          referenceRouteInfo.routes &&
          referenceRouteInfo.routes[0] &&
          referenceRouteInfo.routes[0].legs
        ) {
          const referenceRoutePolylines = referenceRouteInfo.routes[0].legs.map(
            ({ polyline }) => polyline,
          )

          setReferenceRouteSource({
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: referenceRoutePolylines.flatMap((polylineStr) => {
                return polyline
                  .decode(polylineStr)
                  .map(([lat, lng]) => [lng, lat])
              }),
            },
          })

          const driverPolylineResult = await AdHocCommands.readDriverPolyline(
            window.XMPP!,
            routeJid,
            alertId,
          )

          const driverPolylineAndDistance =
            DriverPolylineParser.parse(driverPolylineResult)
          if (driverPolylineAndDistance?.polyline) {
            const referenceRouteCoordinates = polyline
              .decode(driverPolylineAndDistance?.polyline)
              .map(([lat, lng]) => [lng, lat])
            setDriverRouteSource({
              type: "Feature",
              properties: {},
              geometry: {
                type: "LineString",
                coordinates: referenceRouteCoordinates,
              },
            })

            const currentRouteLeftDistance = projectedRoute.routes[0].distance
              ? sumRouteMetrics(fetchedStops).totalDistance
              : 0
            const currentRouteAndDriverDistance =
              driverPolylineAndDistance.distance + currentRouteLeftDistance

            const [lon, lat] = referenceRouteCoordinates.at(-1) as [
              number,
              number,
            ]

            setPopupSettings({
              lat,
              lon,
              referenceRouteDistance: referenceRouteInfo.routes[0].distance,
              currentRouteAndDriverDistance,
              referenceTollCost,
              projectedTollCost,
            })
          }
        }
      }
    } catch (err) {
      console.error("Error fetching reference route alert data:", err)
    }
  }, [referenceRouteAlert])

  useEffect(() => {
    fetchAlertData()
  }, [fetchAlertData, referenceRouteAlert.alertId])

  useEffect(() => {
    showAllLayers()
  }, [referenceRouteSource, driverRouteSource, routeInfo, showAllLayers])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleClose])
  const driverName = driverParticipant
    ? `${driverParticipant?.firstName} ${driverParticipant?.lastName}`
    : undefined
  const alertDate = referenceRouteAlert.updatedAt
    ? format(new Date(referenceRouteAlert.updatedAt), "HH:mm dd/MM/yyyy", {
        locale: getDateFnsLocale(),
      })
    : ""
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
    >
      <DialogContent
        className="max-h-[90vh] sm:max-w-[50vw]"
        backgroundOpacity={50}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("maps.referenceRouteAlert.title")}</DialogTitle>
          <DialogDescription>
            <div className="flex gap-1 pb-4 pt-2 text-muted-foreground">
              {driverName && (
                <>
                  <span>{driverName}</span>
                  <span>|</span>
                </>
              )}
              <span>{alertDate}</span>
            </div>
            <div className="relative mx-auto flex h-[75vh] w-full">
              {stops && (
                <MapView
                  isAlertMap
                  mapViewRef={mapViewRef}
                  sharedStops={stops}
                  sharedRoute={routeInfo}
                  driverJid="shared"
                  isSharedMap
                >
                  {referenceRouteSource && (
                    <Source type="geojson" data={referenceRouteSource}>
                      <Layer {...referenceRouteLayer} />
                    </Source>
                  )}
                  {driverRouteSource && (
                    <Source type="geojson" data={driverRouteSource}>
                      <Layer {...driverRouteLayer} />
                    </Source>
                  )}
                  {driverLocationSource && (
                    <Source type="geojson" data={driverLocationSource}>
                      <Layer {...driverLocationLayer} />
                    </Source>
                  )}
                  {popupSettings && (
                    <Popup
                      anchor="top"
                      longitude={popupSettings.lon}
                      latitude={popupSettings.lat}
                    >
                      <div className="rounded-lg bg-white p-3 shadow-lg dark:bg-gray-800">
                        <div className="grid grid-cols-[auto_auto_auto_auto_auto] items-center gap-x-2">
                          <div className="text-xs font-medium">
                            {Math.round(
                              popupSettings.referenceRouteDistance / 1000,
                            )}{" "}
                            km
                          </div>
                          <div className="text-xs font-medium">
                            {popupSettings.referenceTollCost != null
                              ? `${popupSettings.referenceTollCost.toFixed(2)} EUR`
                              : ""}
                          </div>
                          <div className="text-xs font-medium">
                            {t("maps.referenceRouteAlert.original")}
                          </div>
                          <div className="h-3 w-3 rounded-full bg-green-600"></div>
                          <div></div>

                          <div className="text-xs">
                            {Math.round(
                              popupSettings.currentRouteAndDriverDistance /
                                1000,
                            )}{" "}
                            km
                          </div>
                          <div className="text-xs font-medium">
                            {popupSettings.projectedTollCost != null
                              ? `${popupSettings.projectedTollCost.toFixed(2)} EUR`
                              : ""}
                          </div>
                          <div className="text-xs">
                            {t("maps.referenceRouteAlert.current")}
                          </div>
                          <div className="h-3 w-3 rounded-full bg-red-700"></div>
                          <div className="h-3 w-3 rounded-full bg-blue-600"></div>
                        </div>
                      </div>
                    </Popup>
                  )}
                </MapView>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}

export default ReferenceRouteAlert
